import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import './SplashScreen.css';

const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const controls = useAnimation();

  useEffect(() => {
    // Start logo animation immediately
    controls.start({
      scale: [0.5, 1.1, 1],
      opacity: [0, 1, 1],
      transition: { duration: 1.2, times: [0, 0.6, 1] }
    });
  }, [controls]);

  useEffect(() => {
    // Progress animation
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    // Auto complete after 3.5 seconds
    const timer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 3500);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [onComplete]);

  const floatingParticles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 6 + 2,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 2,
    opacity: Math.random() * 0.5 + 0.1,
  }));

  const shimmerVariants = {
    animate: {
      x: ['-200%', '200%'],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: "linear",
      },
    },
  };

  const ringVariants = {
    animate: (i) => ({
      scale: [1, 1.2, 1],
      rotate: [0, 360],
      opacity: [0.3, 0.6, 0.3],
      transition: {
        duration: 3 + i * 0.5,
        repeat: Infinity,
        ease: "linear",
        delay: i * 0.2,
      },
    }),
  };

  return (
    <motion.div 
      className="splash-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.6 }}
    >
      {/* Animated Background */}
      <div className="splash-background">
        <div className="gradient-bg" />
        <div className="grid-pattern" />
        
        {/* Animated Rings */}
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="ring ring-1"
            custom={i}
            variants={ringVariants}
            animate="animate"
            style={{
              width: 200 + i * 80,
              height: 200 + i * 80,
              borderColor: `rgba(90, 163, 201, ${0.15 - i * 0.04})`,
              borderWidth: 1 + i * 0.5,
            }}
          />
        ))}

        {/* Floating Particles */}
        {floatingParticles.map((particle) => (
          <motion.div
            key={particle.id}
            className="particle"
            initial={{
              x: `${particle.x}%`,
              y: `${particle.y}%`,
              scale: 0,
              opacity: 0,
            }}
            animate={{
              y: [`${particle.y}%`, `${particle.y - 30}%`, `${particle.y}%`],
              scale: [0, 1, 0],
              opacity: [0, particle.opacity, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: "easeInOut",
            }}
            style={{
              width: particle.size,
              height: particle.size,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="splash-content">

        {/* Company Name Image */}
        <motion.div 
          className="company-name-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <motion.div 
            className="company-logo-wrapper"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <img 
              src="/Logo_with_name.png" 
              alt="Tatvam Silverr" 
              className="company-name-image"
            />
          </motion.div>
          
          <motion.div 
            className="company-tagline"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <span className="tagline-dot">●</span>
            <span>Premium Bullion Trading</span>
            <span className="tagline-dot">●</span>
          </motion.div>

          {/* Shimmer Effect */}
          <motion.div 
            className="shimmer-line"
            variants={shimmerVariants}
            animate="animate"
          />
        </motion.div>

        {/* Loading Progress */}
        <motion.div 
          className="loading-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="progress-bar">
            <motion.div 
              className="progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
          <motion.div 
            className="progress-text"
            animate={{
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {progress < 30 && 'Initializing...'}
            {progress >= 30 && progress < 60 && 'Loading markets...'}
            {progress >= 60 && progress < 90 && 'Preparing dashboard...'}
            {progress >= 90 && 'Almost there...'}
          </motion.div>
        </motion.div>

        {/* Version Badge */}
        <motion.div 
          className="version-badge"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ delay: 1.2 }}
        >
          v2.0.0 • Secure Trading
        </motion.div>
      </div>

      {/* Decorative Corner Elements */}
      <motion.div 
        className="corner-decoration top-left"
        initial={{ opacity: 0, x: -20, y: -20 }}
        animate={{ opacity: 0.3, x: 0, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <div className="corner-line" />
      </motion.div>
      <motion.div 
        className="corner-decoration top-right"
        initial={{ opacity: 0, x: 20, y: -20 }}
        animate={{ opacity: 0.3, x: 0, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        <div className="corner-line" />
      </motion.div>
      <motion.div 
        className="corner-decoration bottom-left"
        initial={{ opacity: 0, x: -20, y: 20 }}
        animate={{ opacity: 0.3, x: 0, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <div className="corner-line" />
      </motion.div>
      <motion.div 
        className="corner-decoration bottom-right"
        initial={{ opacity: 0, x: 20, y: 20 }}
        animate={{ opacity: 0.3, x: 0, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <div className="corner-line" />
      </motion.div>
    </motion.div>
  );
};

export default SplashScreen;